import { IRunningOrder, IRunningOrderDogClass, BreedGroupJudgeDto } from "@eagerdog/interfaces";
import { Constants, isUKCConformationUnscheduledLevel } from "@eagerdog/constants";

export interface IJudge {
  judge_element: string;
  judge_level?: string;
  judge_section?: string;
  judge_name: string;
  judge_number: string;
  breed_group?: string;
}

export interface IShowElement {
  checked: boolean;
  label: string;
}

/**
 * The set of UKC Conformation non-licensed class labels (as they appear in showElements).
 * These always belong to the element judge.
 */
const UKC_NON_LICENSED_LABELS = new Set<string>([
  Constants.dog_class_element.novice_puppy,       // "Novice Puppy"
  Constants.dog_class_element.veteran,             // "Veteran"
  Constants.dog_class_element.brood_bitch_stud_dog,// "Brood Bitch/Stud Dog"
  Constants.dog_class_element.brace,               // "Brace"
  Constants.dog_class_element.total_dog,           // "Total Dog"
  Constants.dog_class_element.total_junior,        // "Total Junior"
  Constants.dog_class_element.other_racing_bred_dogs, // "Other: Racing Bred Dogs"
]);

const dc = (show_element: string, level?: string, start_letter?: string, end_letter?: string): IRunningOrderDogClass => ({
  type: Constants.running_order_dog_class_type.dog_class,
  show_element,
  ...(level ? { level } : {}),
  ...(start_letter ? { start_letter } : {}),
  ...(end_letter ? { end_letter } : {}),
});

/**
 * Create running order for UKC Conformation shows.
 *
 * Rules:
 *  1. Each breed group with an assigned judge gets that judge + a Conformation dog-class entry.
 *  2. Breed groups without a specific judge fall to the element judge.
 *  3. When breedGroups is empty/absent, the element judge gets a single "Conformation" entry.
 *  4. Non-licensed classes (Brace, Brood Bitch/Stud Dog, …) always go under the element judge.
 *     Novice Puppy and Veteran are judged with their breed, and Total Dog / Total Junior are
 *     fee-only, so none of those get a class of their own.
 *  5. Junior Showmanship always goes under the element judge.
 *  6. Judges with the same name + number are merged into a single running-order entry
 *     (and therefore a single ring when the caller splits by unique judges).
 */
export function createUKCConformationRunningOrder(
  judges: IJudge[],
  showElements: IShowElement[],
  breedGroups: BreedGroupJudgeDto[] = []
): IRunningOrder[] {
  // Prefer the "same judge for all" entry (empty judge_element), then any named judge.
  // Unassigned breed groups fall back to this judge — they must not create an empty RO row.
  const elementJudge =
    judges.find((j) => j.judge_element === "" && (j.judge_name || "").trim() !== "") ||
    judges.find((j) => (j.judge_name || "").trim() !== "") ||
    judges[0] ||
    { judge_name: "", judge_number: "" };
  const elementKey = `${elementJudge.judge_name}|${elementJudge.judge_number}`;

  const judgeMap = new Map<string, IRunningOrder>();

  const getOrCreate = (name: string, number: string): IRunningOrder => {
    const key = `${name}|${number}`;
    if (!judgeMap.has(key)) {
      judgeMap.set(key, { judge_name: name, judge_number: number, dog_classes: [] });
    }
    return judgeMap.get(key)!;
  };

  // Always ensure the element judge entry exists first (it anchors non-licensed classes).
  getOrCreate(elementJudge.judge_name, elementJudge.judge_number);

  // --- Breed group classes ---
  if (breedGroups.length > 0) {
    for (const bg of breedGroups) {
      const hasJudge = bg.judge_name && bg.judge_name.trim() !== "";
      const entry = hasJudge
        ? getOrCreate(bg.judge_name, bg.judge_number || "")
        : getOrCreate(elementJudge.judge_name, elementJudge.judge_number);

      entry.dog_classes.push(dc(Constants.dog_class_element.conformation, bg.breed_group, bg.start_letter, bg.end_letter));
    }
  } else {
    // No breed group data – add a bare Conformation entry under the element judge.
    judgeMap.get(elementKey)!.dog_classes.push(dc(Constants.dog_class_element.conformation));
  }

  // --- Non-licensed + Junior Showmanship (always element judge) ---
  const elementEntry = judgeMap.get(elementKey)!;

  for (const se of showElements) {
    if (!se.checked || isUKCConformationUnscheduledLevel(se.label)) continue;

    if (UKC_NON_LICENSED_LABELS.has(se.label)) {
      elementEntry.dog_classes.push(
        dc(Constants.dog_class_element.conformation_non_licensed, se.label)
      );
    } else if (se.label === Constants.dog_class_element.junior_showmanship) {
      elementEntry.dog_classes.push(dc(Constants.dog_class_element.junior_showmanship));
    }
    // "Conformation" show element itself is handled via the breed groups loop above.
  }

  return Array.from(judgeMap.values()).filter(
    (ro) => (ro.judge_name || "").trim() !== ""
  );
}
